// Composer Studio — form-import P2·P3 순수 연산
//
// ① 데이터 연결 후보 제안(P2): suggest-bindings 요청 대상 필드 추출 + 응답 후보의
//    prefillFrom 일괄 적용(body 바인딩 정본 — 파생 위젯/서버 collectPrefillBindings가
//    body Binding.prefillFrom을 읽는다).
// ② 반복 영역 제안 원클릭(P3): importMeta.repeatHints 1건을 기존 수동 플로우 산출물과
//    동형으로 적용 — 수동 데이터셋(kind:'manual') 골격 생성(handleCreateManualDataSource
//    형상) + 반복영역 지정(addRepeatRegion) + 소진된 hint 제거.
// ③ 초안 단위 일괄 적용(S5): ②를 초안의 전 힌트에 순차 적용(applyAllRepeatHints) + 그
//    적용에 필요한 라벨 맵 산출(writeFormLabelsOf) — 배치 결과 모달이 **로드하지 않은**
//    초안에 대해 쓰므로, 에디터 memo에 기대지 않는 순수 함수여야 한다.
//
// 각 연산은 한 번의 반환으로 묶여 호출부가 `apply((t) => op(t, …))` 단일 producer
// (=undo 1스텝)로 쓴다. 설계 정본: docs/decisions/2026-07-27-form-import-p2-binding-suggest.md
import type {
  Binding,
  CatalogIndex,
  ComposerWidget,
  DataSourceDef,
  DataSourceFieldMap,
  ReportTemplate,
  TemplateImportMeta,
} from '@safetyhub/shared';
import { isRepeatHintRowRangeValid } from '@safetyhub/shared';
import {
  addRepeatRegion,
  createWriteFormBindingFilter,
  deriveWriteForm,
  updateCell,
  updateNode,
  walkBindings,
} from '@safetyhub/report-composer';
import { findNodeById } from './findNodeById';

/** 서버 BindingSuggestField와 1:1 — 에디터가 현재 편집 상태에서 추출해 보낸다. */
export interface BindingSuggestFieldInput {
  bindingKey: string;
  label?: string;
  context?: string;
}

/** importMeta.repeatHints 원소(공유 타입에서 도출 — 별도 재정의 금지). */
export type ImportRepeatHint = TemplateImportMeta['repeatHints'][number];

/**
 * bindingKey → 작성폼 라벨 맵 — 파생 작성폼(`deriveWriteForm`) 기준.
 * suggest 요청 라벨·반복 골격 필드 라벨의 **단일 정본**이다.
 *
 * 에디터(EditorPage.buildWriteFormLabels)는 이미 memo해 둔 파생본이 있어 그걸 쓰지만,
 * 결과 모달처럼 **로드하지 않은 템플릿**을 다루는 호출부는 템플릿만 들고 여기로 온다. 규칙을
 * 두 벌로 두면 같은 초안에 에디터로 적용한 라벨과 모달로 적용한 라벨이 갈리므로 여기로 모은다.
 *
 * ⚠️ 2026-10-02(S6): 종전에는 `mergeWriteForm(deriveWriteForm(t, catalog), t.writeForm)`으로
 * 저장된 flat override를 덮었는데 그 축(`ReportTemplate.writeForm`)이 철거됐다 — 라벨 정본은
 * 파생(본문 바인딩·변수·dataDef)과 저작 트리뿐이다.
 *
 * ⚠️ **「rc `applyOverride`와 같은 일을 하니 산출이 같다」로 읽지 말 것** — 두 함수의 label 규칙은
 * 실제로 **달랐다**: admin `mergeWriteForm`은 `override.label ?? d.label`(키가 없으면 **파생 유지**)이고
 * rc `applyOverride`는 `node.label = override.label`(**조건 없이 덮어** 키가 없으면 파생 라벨을 지운다).
 * 산출이 갈리지 않은 이유는 코드가 같아서가 아니라 **prod flat 484노드가 `label` 키를 전수 보유해
 * 그 차이가 발현하지 않았기 때문**이다(2026-09-30·10-02 실측: flat 노드가 갖는 키는 `id`·`bindingKey`·
 * `widget`·`label` 넷뿐 484 전수 · 트리와 겹치는 208의 `label` 불일치 0건). 그 비대칭이 곧
 * `applyOverride` JSDoc이 경고하던 잠재 결함이고, 덮는 채널 자체가 사라져 함께 소멸했다.
 *
 * catalog는 없어도 동작한다(deriveWriteForm의 옵셔널 인자와 같은 계약) — 카탈로그 연결
 * 바인딩의 위젯 메타·라벨이 덜 채워질 뿐, 수동 입력 바인딩 라벨은 그대로다.
 */
export function writeFormLabelsOf(
  t: ReportTemplate,
  catalog?: CatalogIndex,
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const f of deriveWriteForm(t, catalog)) {
    // `InputWidget.label`은 옵셔널이다(트리 저작은 labelPlacement:'none'으로 라벨 없는 위젯을
    // 허용한다 — 2026-09-30 S2로 파생 산출 타입이 그쪽으로 합쳐졌다). 파생 경로는 세 생성 자리
    // 모두 label을 채우므로 실제로 비는 일은 없지만, 비면 rc 스캐폴드와 같은 폴백(bindingKey)을
    // 쓴다 — 라벨 맵의 값은 화면 표시용이라 빈 문자열보다 키가 낫다.
    labels[f.bindingKey] = f.label ?? f.bindingKey;
  }
  return labels;
}

/** 반복 제안 적용으로 만드는 수동 데이터셋의 라벨 길이 상한(속성창 드롭다운 가독성). */
const REPEAT_DS_LABEL_MAX = 40;
/** reason이 공백뿐이면 쓰는 라벨(스키마상 min(1)이라 실전에선 거의 안 탄다). */
const REPEAT_DS_LABEL_FALLBACK = '반복 기입 행';

/**
 * 행 범위가 표 격자를 벗어난 제안의 거부 사유 — 적용 시도(여기)와 배지의 "적용 불가" 표시
 * (EditorPage)가 **같은 문장**을 쓴다. 두 벌로 두면 같은 상태를 두 문구로 설명하게 된다.
 */
export const REPEAT_HINT_ROW_RANGE_ERROR =
  '제안 행 범위가 표 밖입니다(행 구조가 바뀐 것 같습니다).';

/**
 * 작성폼 위젯 트리에서 bindingKey → 소속 그룹 제목 맵을 만든다(suggest 요청 context용).
 * 그룹(title)·formTable(title) 문맥만 가볍게 수집 — repeat children(행 상대키)·modalTrigger
 * (모달 스코프)는 본문 바인딩과 키 공간이 달라 하강하지 않는다.
 */
export function deriveWidgetGroupContexts(
  tree: ComposerWidget[] | undefined,
): Record<string, string> {
  const map: Record<string, string> = {};
  const visit = (widgets: ComposerWidget[], context: string | undefined): void => {
    for (const w of widgets) {
      switch (w.kind) {
        case 'group':
          visit(w.children, w.title ?? context);
          break;
        case 'branch':
          for (const slot of w.branches) visit(slot.children, context);
          break;
        case 'formTable': {
          const ctx = w.title ?? context;
          if (!ctx) break;
          for (const row of w.rows) {
            if (row.type !== 'fixed') continue;
            for (const cell of row.cells) {
              if (cell.widget && !(cell.widget.bindingKey in map)) {
                map[cell.widget.bindingKey] = ctx;
              }
            }
          }
          break;
        }
        case 'input':
          if (context && !(w.bindingKey in map)) map[w.bindingKey] = context;
          break;
        default:
          break;
      }
    }
  };
  visit(tree ?? [], undefined);
  return map;
}

/**
 * suggest-bindings 요청 대상 필드 수집 — 수동 입력(`source:'input'`) 바인딩만.
 *
 * - 작성폼 파생 대상 판정은 정본 술어(createWriteFormBindingFilter) 재사용 — 반복영역 밴드
 *   셀/repeat 서브트리(행 필드)·compute 입력은 자동 제외.
 * - 이미 자동 채움 출처가 있는 키(명시 prefillFrom 또는 catalogKey 연결)는 재제안이 무의미해
 *   전체 제외한다(bindingPrefillMap과 동일 규칙: prefillFrom ?? catalogKey 합성).
 * - bindingKey 단위 dedup(트리 순서 유지).
 */
export function collectBindingSuggestFields(
  template: ReportTemplate,
  labels: Record<string, string>,
  contexts: Record<string, string>,
): BindingSuggestFieldInput[] {
  const isWriteFormBinding = createWriteFormBindingFilter(template.body);
  // 1패스: 이미 자동 채움 출처가 있는 키 수집(같은 키의 다른 바인딩에 있어도 제외 대상).
  const prefilled = new Set<string>();
  walkBindings(template.body, (b) => {
    if (b.prefillFrom || b.catalogKey) prefilled.add(b.bindingKey);
  });
  // 2패스: 후보 수집.
  const seen = new Set<string>();
  const fields: BindingSuggestFieldInput[] = [];
  walkBindings(template.body, (b, owner) => {
    if (!isWriteFormBinding(owner)) return;
    if (b.source !== 'input' || b.catalogKey || b.repeatOver) return;
    if (prefilled.has(b.bindingKey) || seen.has(b.bindingKey)) return;
    seen.add(b.bindingKey);
    const label = labels[b.bindingKey];
    const context = contexts[b.bindingKey];
    fields.push({
      bindingKey: b.bindingKey,
      ...(label ? { label } : {}),
      ...(context ? { context } : {}),
    });
  });
  return fields;
}

/**
 * 후보 적용 — 대상 bindingKey의 모든 수동 입력 바인딩에 `prefillFrom`을 설정한다.
 *
 * 같은 키가 여러 셀/노드에 있으면 전부에 기록한다(읽기 규칙은 첫 바인딩 우선이지만, 일부만
 * 기록하면 그 셀 삭제 시 자동 채움이 조용히 소실되는 드리프트가 생긴다). 이미 catalogKey
 * 연결된 바인딩(카탈로그 경로 우선이라 prefillFrom 무의미)은 건드리지 않는다.
 * 같은 bindingKey에 후보가 여럿이면 첫 후보만 채택(순차 적용=마지막 승리의 비결정 방지).
 *
 * ⚠️ 대상 범위는 후보를 **수집한 범위와 같아야 한다**(collectBindingSuggestFields와 동일 술어).
 * form-import 초안은 라벨 슬러그로 키를 만들어 같은 bindingKey가 고정 셀과 반복영역 밴드 셀에
 * 공존할 수 있고, 키 문자열만 보고 body 전역에 쓰면 제안하지도 않은 반복 행 필드까지 덮어쓴다.
 */
export function applyPrefillSuggestions(
  template: ReportTemplate,
  items: { bindingKey: string; catalogKey: string; select?: string }[],
): { next: ReportTemplate; appliedKeys: string[] } {
  const byKey = new Map<string, { catalogKey: string; select?: string }>();
  for (const it of items) {
    if (!byKey.has(it.bindingKey)) {
      byKey.set(it.bindingKey, {
        catalogKey: it.catalogKey,
        ...(it.select ? { select: it.select } : {}),
      });
    }
  }
  // 좌표 선수집 후 순차 적용(좌표는 안정 식별자 — renameDataSourceFieldEverywhere 관례).
  const isWriteFormBinding = createWriteFormBindingFilter(template.body);
  const cellTargets: { tableId: string; row: number; col: number; binding: Binding }[] = [];
  const nodeTargets: { nodeId: string; binding: Binding }[] = [];
  walkBindings(template.body, (b, owner) => {
    if (!isWriteFormBinding(owner)) return;
    if (!byKey.has(b.bindingKey)) return;
    if (b.source !== 'input' || b.catalogKey || b.repeatOver) return;
    if (owner.source === 'cell' && owner.cell) {
      cellTargets.push({ ...owner.cell, binding: b });
    } else if (owner.source === 'node' && owner.nodeId) {
      nodeTargets.push({ nodeId: owner.nodeId, binding: b });
    }
  });
  let next = template;
  const applied = new Set<string>();
  for (const tg of cellTargets) {
    const pf = byKey.get(tg.binding.bindingKey);
    if (!pf) continue;
    next = updateCell(next, tg.tableId, tg.row, tg.col, {
      binding: { ...tg.binding, prefillFrom: pf },
    });
    applied.add(tg.binding.bindingKey);
  }
  for (const tg of nodeTargets) {
    const pf = byKey.get(tg.binding.bindingKey);
    if (!pf) continue;
    next = updateNode(next, tg.nodeId, { binding: { ...tg.binding, prefillFrom: pf } });
    applied.add(tg.binding.bindingKey);
  }
  return { next, appliedKeys: [...applied] };
}

/**
 * 후보 적용(완전 연결) — 대상 bindingKey의 수동 입력 바인딩을 **카탈로그 바인딩으로 교체**한다.
 *
 * 제품 원칙(2026-07-27 사용자 확정): "한 번의 점검 + 최소 수동입력"이 강점이므로, 시스템이
 * 아는 값은 작성자 입력이 아니라 **시스템 데이터 직접 연결**이 기본이다. 연결되면:
 *  - 바인딩이 catalogKey/select 경로로 값을 읽는다(bindingKey는 저장 키로 유지 — rename 시
 *    작성 위젯 트리 참조가 끊기므로 유지가 안전).
 *  - deriveWriteForm 파생에서 빠져 작성 페이지 입력칸이 사라진다(수동입력 감소).
 *  - 작성폼 트리에 그 키의 위젯이 배치돼 있었다면 orphan이 된다 — 수동으로 카탈로그 연결할
 *    때와 동일한 기존 규약(트리 UI의 orphan 표시로 사람이 정리). 자동 삭제하지 않는다.
 *  - prefillFrom은 무의미해져 제거(카탈로그 경로가 우선이라 죽은 메타).
 * 이미 catalogKey가 있는 바인딩은 건드리지 않는다. 같은 키 복수 후보는 첫 후보 채택.
 *
 * ⚠️ applyPrefillSuggestions와 같은 이유로 대상 범위는 수집 범위(작성폼 파생 대상 + 비repeat)에
 * 묶는다 — 같은 키의 반복영역 밴드 셀을 카탈로그 단일 값으로 고정시키면 표 열이 통째로 죽는다.
 */
export function applyCatalogConnectSuggestions(
  template: ReportTemplate,
  items: { bindingKey: string; catalogKey: string; select?: string }[],
): { next: ReportTemplate; appliedKeys: string[] } {
  const byKey = new Map<string, { catalogKey: string; select?: string }>();
  for (const it of items) {
    if (!byKey.has(it.bindingKey)) {
      byKey.set(it.bindingKey, {
        catalogKey: it.catalogKey,
        ...(it.select ? { select: it.select } : {}),
      });
    }
  }
  const connect = (b: Binding, target: { catalogKey: string; select?: string }): Binding => {
    const next: Binding = {
      ...b,
      catalogKey: target.catalogKey,
      lifecycle: b.lifecycle ?? 'snapshot',
    };
    if (target.select) next.select = target.select;
    else delete next.select;
    delete next.prefillFrom;
    return next;
  };
  const isWriteFormBinding = createWriteFormBindingFilter(template.body);
  const cellTargets: { tableId: string; row: number; col: number; binding: Binding }[] = [];
  const nodeTargets: { nodeId: string; binding: Binding }[] = [];
  walkBindings(template.body, (b, owner) => {
    if (!isWriteFormBinding(owner)) return;
    if (!byKey.has(b.bindingKey)) return;
    if (b.source !== 'input' || b.catalogKey || b.repeatOver) return;
    if (owner.source === 'cell' && owner.cell) {
      cellTargets.push({ ...owner.cell, binding: b });
    } else if (owner.source === 'node' && owner.nodeId) {
      nodeTargets.push({ nodeId: owner.nodeId, binding: b });
    }
  });
  let next = template;
  const applied = new Set<string>();
  for (const tg of cellTargets) {
    const target = byKey.get(tg.binding.bindingKey);
    if (!target) continue;
    next = updateCell(next, tg.tableId, tg.row, tg.col, {
      binding: connect(tg.binding, target),
    });
    applied.add(tg.binding.bindingKey);
  }
  for (const tg of nodeTargets) {
    const target = byKey.get(tg.binding.bindingKey);
    if (!target) continue;
    next = updateNode(next, tg.nodeId, { binding: connect(tg.binding, target) });
    applied.add(tg.binding.bindingKey);
  }
  return { next, appliedKeys: [...applied] };
}

/**
 * 후보 필드 목록에 필드 1건을 **트리 순서 위치**에 삽입한다(해제 복귀용).
 *
 * 목록은 collectBindingSuggestFields의 트리 순서를 따르므로, 맨 뒤 append가 아니라 본문
 * 바인딩 순서 기준 제자리에 넣어야 원래 정렬이 유지된다(2026-07-27 사용자 보고). 이미 있으면
 * 원본 참조 반환. 순서를 모르는 키(본문에서 사라진 경우)는 맨 뒤.
 */
export function insertBindingSuggestField(
  template: ReportTemplate,
  fields: BindingSuggestFieldInput[],
  field: BindingSuggestFieldInput,
): BindingSuggestFieldInput[] {
  if (fields.some((f) => f.bindingKey === field.bindingKey)) return fields;
  const order = new Map<string, number>();
  let i = 0;
  walkBindings(template.body, (b) => {
    if (!order.has(b.bindingKey)) order.set(b.bindingKey, i++);
  });
  const idx = order.get(field.bindingKey) ?? Number.POSITIVE_INFINITY;
  const pos = fields.findIndex((f) => (order.get(f.bindingKey) ?? Number.POSITIVE_INFINITY) > idx);
  return pos === -1
    ? [...fields, field]
    : [...fields.slice(0, pos), field, ...fields.slice(pos)];
}

/** 적용된 자동 채움 출처 1건 — 모달 "적용된 연결" 섹션 표시용. */
export interface AppliedBindingSummary {
  bindingKey: string;
  label?: string;
  /** connect=카탈로그 직접 연결(catalogKey) / prefill=자동 채움(prefillFrom) */
  mode: 'connect' | 'prefill';
  catalogKey: string;
  select?: string;
}

/**
 * 적용된 연결/채움 현황 수집 — 모달이 "적용된 연결" 섹션으로 보여 주고 해제 대상을 고른다.
 *
 * 이 모달로 적용한 것과 손으로 연결한 카탈로그 바인딩을 구분 저장하지 않으므로 둘 다 보인다
 * (양식의 자동 채움 현황판 역할 — 2026-07-27 사용자 합의). 수집 범위는 suggest와 같은 우주:
 * 작성폼 대상 판정 정본 술어(createWriteFormBindingFilter — 반복 밴드/compute 입력 제외) +
 * repeat 순회 바인딩(repeatOver) 제외. catalogKey가 있으면 connect(카탈로그 경로 우선 규약),
 * 없고 prefillFrom만 있으면 prefill. bindingKey 단위 dedup(트리 순서 유지).
 */
export function collectAppliedBindings(
  template: ReportTemplate,
  labels: Record<string, string>,
): AppliedBindingSummary[] {
  const isWriteFormBinding = createWriteFormBindingFilter(template.body);
  const seen = new Set<string>();
  const out: AppliedBindingSummary[] = [];
  walkBindings(template.body, (b, owner) => {
    if (!isWriteFormBinding(owner)) return;
    if (b.repeatOver || seen.has(b.bindingKey)) return;
    const src = b.catalogKey
      ? { mode: 'connect' as const, catalogKey: b.catalogKey, select: b.select }
      : b.prefillFrom
        ? {
            mode: 'prefill' as const,
            catalogKey: b.prefillFrom.catalogKey,
            select: b.prefillFrom.select,
          }
        : null;
    if (!src) return;
    seen.add(b.bindingKey);
    const label = labels[b.bindingKey];
    out.push({
      bindingKey: b.bindingKey,
      ...(label ? { label } : {}),
      mode: src.mode,
      catalogKey: src.catalogKey,
      ...(src.select ? { select: src.select } : {}),
    });
  });
  return out;
}

/**
 * 자동 채움 출처 해제 — 대상 bindingKey의 모든 바인딩에서 catalogKey/select/prefillFrom을
 * 제거해 수동 입력으로 되돌린다(연결·채움 공용 역연산).
 *
 * 같은 키의 모든 셀/노드에 일괄 적용(applyCatalogConnectSuggestions와 동일 좌표 방식 —
 * 일부만 남기면 드리프트). 레거시 경로(api/aiAction)가 없고 source도 없으면 'input'을
 * 부여해 suggest 대상으로 복귀시킨다(레거시 필드가 있으면 그 동작 보존 — 건드리지 않음).
 *
 * ⚠️ 범위는 collectAppliedBindings(=목록에 보여 준 것)와 같아야 한다. 반복영역 밴드 셀은
 * 목록에 없으므로 해제 대상도 아니다 — 같은 키라는 이유로 손대면 사용자가 보지도 못한
 * 반복 소스 연결이 조용히 끊긴다.
 */
export function disconnectAppliedBindings(
  template: ReportTemplate,
  bindingKeys: string[],
): { next: ReportTemplate; disconnectedKeys: string[] } {
  const keys = new Set(bindingKeys);
  const clear = (b: Binding): Binding => {
    const next: Binding = { ...b };
    delete next.catalogKey;
    delete next.select;
    delete next.prefillFrom;
    if (!next.source && !next.api && !next.aiAction) next.source = 'input';
    return next;
  };
  const cellTargets: { tableId: string; row: number; col: number; binding: Binding }[] = [];
  const nodeTargets: { nodeId: string; binding: Binding }[] = [];
  const isWriteFormBinding = createWriteFormBindingFilter(template.body);
  walkBindings(template.body, (b, owner) => {
    if (!isWriteFormBinding(owner)) return;
    if (!keys.has(b.bindingKey) || b.repeatOver) return;
    if (!b.catalogKey && !b.prefillFrom) return;
    if (owner.source === 'cell' && owner.cell) {
      cellTargets.push({ ...owner.cell, binding: b });
    } else if (owner.source === 'node' && owner.nodeId) {
      nodeTargets.push({ nodeId: owner.nodeId, binding: b });
    }
  });
  let next = template;
  const disconnected = new Set<string>();
  for (const tg of cellTargets) {
    next = updateCell(next, tg.tableId, tg.row, tg.col, { binding: clear(tg.binding) });
    disconnected.add(tg.binding.bindingKey);
  }
  for (const tg of nodeTargets) {
    next = updateNode(next, tg.nodeId, { binding: clear(tg.binding) });
    disconnected.add(tg.binding.bindingKey);
  }
  return { next, disconnectedKeys: [...disconnected] };
}

// ── 데이터 연결 후보 캐시 (P2 후속, 2026-07-27 사용자 요청) ─────────────
// AI 호출 결과를 템플릿 키별로 localStorage에 보관해, 모달을 다시 열 때 호출 없이 직전
// 후보를 보여 준다. 새 호출은 명시적 "재분석"으로만 — 비용·대기시간 절감 + 적용하다 만
// 목록을 이어서 볼 수 있게. 저장/파싱은 순수 함수로 분리(vitest), I/O는 모달이 담당.

/** 캐시 1건 — 후보 목록 + 분석 시각(표시용). */
export interface BindingSuggestCacheEntry {
  suggestions: {
    bindingKey: string;
    catalogKey: string;
    select: string;
    confidence: number;
    reason: string;
  }[];
  fetchedAt: number;
}

/** localStorage 키 — 템플릿 키별 분리(다른 템플릿의 후보가 섞이지 않게). */
export function bindingSuggestCacheKey(templateKey: string): string {
  return `composer-binding-suggest:${templateKey}`;
}

/**
 * 캐시 원문 파싱(신뢰 불가 저장소 — 형상 검증 후만 수용). 형상이 깨졌으면 null(캐시 없음
 * 취급 — 호출측이 새 분석).
 *
 * 후보는 **전체 보관**하고 현재 필드와의 교집합은 표시 시점(모달 rows)에 거른다 — 파싱에서
 * 걸러 버리면 연결돼 목록에서 빠졌던 필드를 해제로 되살렸을 때 직전 후보가 소실된다
 * (2026-07-27 사용자 보고: 해제 복귀 행에 연결 버튼 없음).
 */
export function parseBindingSuggestCache(raw: string | null): BindingSuggestCacheEntry | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const entry = parsed as { suggestions?: unknown; fetchedAt?: unknown };
  if (!Array.isArray(entry.suggestions) || typeof entry.fetchedAt !== 'number') return null;
  const valid = entry.suggestions.filter(
    (s): s is BindingSuggestCacheEntry['suggestions'][number] =>
      typeof s === 'object' &&
      s !== null &&
      typeof (s as { bindingKey?: unknown }).bindingKey === 'string' &&
      typeof (s as { catalogKey?: unknown }).catalogKey === 'string' &&
      typeof (s as { select?: unknown }).select === 'string' &&
      typeof (s as { confidence?: unknown }).confidence === 'number' &&
      typeof (s as { reason?: unknown }).reason === 'string',
  );
  return { suggestions: valid, fetchedAt: entry.fetchedAt };
}

/**
 * 새 분석 결과를 직전 캐시와 병합 — **요청에 포함됐던 필드의 후보만 갱신**한다.
 *
 * 재분석 요청에는 이미 연결된 필드가 빠지므로, 결과로 캐시를 통째로 교체하면 그 필드들의
 * 직전 후보가 소실돼 해제 복귀 시 "찾지 못했습니다"가 된다(2026-07-27 사용자 보고 — 사업장명).
 * 요청에 없던 키의 직전 후보는 보존하고, 요청에 있던 키는 새 결과가 정본(새 결과에 없으면
 * "분석했지만 못 찾음"으로 정직하게 비움). fresh와 겹치는 키의 잔존본은 중복 방지로 제외.
 */
export function mergeBindingSuggestions(
  fresh: BindingSuggestCacheEntry['suggestions'],
  prev: BindingSuggestCacheEntry | null,
  requestedKeys: Iterable<string>,
): BindingSuggestCacheEntry['suggestions'] {
  if (!prev) return fresh;
  const exclude = new Set(requestedKeys);
  for (const s of fresh) exclude.add(s.bindingKey);
  return [...fresh, ...prev.suggestions.filter((s) => !exclude.has(s.bindingKey))];
}

/** importMeta에서 hint 1건 제거 — 전부 소진되면 importMeta 자체를 제거(저장 시 null 해제). */
export function removeImportRepeatHint(
  t: ReportTemplate,
  hint: ImportRepeatHint,
): ReportTemplate {
  const hints = t.importMeta?.repeatHints ?? [];
  const rest = hints.filter(
    (h) =>
      !(
        h.tableNodeId === hint.tableNodeId &&
        h.rowStart === hint.rowStart &&
        h.rowEnd === hint.rowEnd
      ),
  );
  if (rest.length === hints.length) return t;
  if (rest.length === 0) {
    const next = { ...t };
    delete next.importMeta;
    return next;
  }
  return { ...t, importMeta: { repeatHints: rest } };
}

/**
 * 반복 영역 제안 원클릭 적용 — 기존 수동 플로우 산출물과 동형:
 *  1. 수동 데이터셋(kind:'manual') 선언 생성 — handleCreateManualDataSource와 동일 형상 +
 *     밴드 행 셀 바인딩들을 필드 골격으로(addDataSourceColumn의 선언 필드 형상:
 *     `{field, label, from:'', widget:'text', editable:true}`; 셀 바인딩은 이미 있어 그대로 정본).
 *  2. 반복영역 `{over, rowStart, rowEnd}` 추가(addRepeatRegion — 범위 검증 동일).
 *  3. 소진된 hint를 importMeta에서 제거.
 *
 * 실패(표 부재/행 범위 밖/기존 반복영역 겹침) 시 원본 참조 반환 + error 메시지(no-op 히스토리 방지).
 * 작성 페이지 위젯 트리 정리(기존 flat 입력 → orphan)와 행 데이터셋 위젯 배치는 기존 트리
 * UI(orphan 표시·미배치 풀)로 사람이 확정한다 — 호출부가 안내한다.
 */
export function applyRepeatHintToTemplate(
  t: ReportTemplate,
  hint: ImportRepeatHint,
  labels: Record<string, string>,
): { next: ReportTemplate; dsKey?: string; fieldCount?: number; error?: string } {
  const table = findNodeById(t.body, hint.tableNodeId);
  if (table?.kind !== 'table') {
    return { next: t, error: '제안 대상 표를 찾을 수 없습니다(삭제되었을 수 있습니다).' };
  }
  // 판정은 서버 변환기(초안에 싣기 전 검증)와 같은 함수를 쓴다 — 기준이 갈리면 변환기가
  // 실은 힌트를 에디터가 매번 거부하는 상태가 된다(2026-08-19).
  if (!isRepeatHintRowRangeValid(hint, table.rowCount)) {
    return { next: t, error: REPEAT_HINT_ROW_RANGE_ERROR };
  }
  const overlaps = (table.repeatRegions ?? []).some(
    (r) =>
      Math.min(r.rowStart, r.rowEnd) <= hint.rowEnd &&
      Math.max(r.rowStart, r.rowEnd) >= hint.rowStart,
  );
  if (overlaps) {
    return { next: t, error: '그 행 범위에 이미 반복영역이 있습니다 — 제안을 무시하세요.' };
  }
  // 밴드 행 셀 바인딩 → 필드 골격(행·열 순서, bindingKey dedup).
  const bandCells = table.cells
    .filter((c) => c.row >= hint.rowStart && c.row <= hint.rowEnd && c.binding?.bindingKey)
    .sort((a, b) => a.row - b.row || a.col - b.col);
  const seen = new Set<string>();
  const fields: DataSourceFieldMap[] = [];
  for (const c of bandCells) {
    const key = c.binding?.bindingKey;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    fields.push({ field: key, label: labels[key] ?? key, from: '', widget: 'text', editable: true });
  }
  // 데이터셋 key 발급 — dataSources/dataDefs 양쪽 키 공간과 충돌 회피(양방향 충돌 검사 규약).
  const taken = new Set<string>([
    ...(t.dataSources ?? []).map((d) => d.key),
    ...(t.dataDefs ?? []).map((d) => d.key),
  ]);
  let dsKey = 'repeat-rows';
  let n = 2;
  while (taken.has(dsKey)) dsKey = `repeat-rows-${n++}`;
  // 라벨은 표별로 갈라야 한다 — 한 초안의 여러 표에 제안을 적용하면 데이터 소스 목록에 같은
  // 이름만 늘어서 어느 표의 것인지 구분할 수 없다(2026-08-19 dev 실측: 표 2건 적용 →
  // 드롭다운에 '반복 기입 행' 2개). AI가 표별로 쓴 reason을 라벨로 삼고, 그마저 겹치면
  // key와 같은 방식으로 suffix를 붙인다. reason은 최대 500자까지 오므로 라벨 길이로 자른다.
  // ⚠️ `?? ''` 필수 — importMeta는 DB에 opaque JSON으로 들어 있어 타입이 보증되지 않는다.
  // reason 없이 저장된 레거시 초안(zod 검증 이전 경로)에서 `.trim()`이 TypeError로 터지는데,
  // 호출부 중 배지 경로(EditorPage.handleApplyImportRepeatHint)는 try/catch 밖이라 그대로 크래시한다.
  const baseLabel =
    (hint.reason ?? '').trim().slice(0, REPEAT_DS_LABEL_MAX) || REPEAT_DS_LABEL_FALLBACK;
  const takenLabels = new Set<string>((t.dataSources ?? []).map((d) => d.label));
  let dsLabel = baseLabel;
  let ln = 2;
  while (takenLabels.has(dsLabel)) dsLabel = `${baseLabel} (${ln++})`;
  const def: DataSourceDef = {
    key: dsKey,
    label: dsLabel,
    cardinality: 'list',
    kind: 'manual',
    primary: '',
    fields,
    editMode: 'editable',
    allowAddRemove: true,
  };
  let next: ReportTemplate = { ...t, dataSources: [...(t.dataSources ?? []), def] };
  next = addRepeatRegion(next, table.id, {
    over: dsKey,
    rowStart: hint.rowStart,
    rowEnd: hint.rowEnd,
  });
  next = removeImportRepeatHint(next, hint);
  return { next, dsKey, fieldCount: fields.length };
}

/** 일괄 적용에서 성공한 제안 1건. */
export interface RepeatHintApplied {
  hint: ImportRepeatHint;
  dsKey: string;
  fieldCount: number;
}

/** 일괄 적용에서 건너뛴 제안 1건 — 사유는 applyRepeatHintToTemplate의 error 문구 그대로. */
export interface RepeatHintFailure {
  hint: ImportRepeatHint;
  error: string;
}

/**
 * importMeta.repeatHints를 **전부 순차 적용**한다(초안 카드의 일괄 적용용).
 *
 * - 대상은 호출 시점 `template.importMeta.repeatHints`의 **스냅샷**이다. applyRepeatHintToTemplate이
 *   성공할 때마다 importMeta에서 소진 항목을 지우므로, 살아 있는 배열을 순회하면 인덱스가 밀린다.
 * - **전부 아니면 전무가 아니다.** 힌트 하나가 무효(표 삭제·행 범위 밖·기존 반복영역 겹침)인 것은
 *   정상 케이스이고, 그 한 건 때문에 나머지를 버리면 사람이 다시 하나씩 눌러야 한다. 실패 건은
 *   `failures`로 모아 보고하고 importMeta에 **남긴다**(에디터에서 다시 판단할 수 있도록).
 * - 실패는 원본 참조를 돌려주므로 누적 템플릿(`next`)에 아무 영향이 없다.
 * - labels는 **적용 전 템플릿 기준으로 한 번** 만들어 넘긴다(호출부 책임). 반복영역이 생기면
 *   그 밴드 셀은 파생 작성폼에서 빠져 라벨을 잃으므로, 중간에 다시 계산하면 뒤쪽 표의 필드
 *   라벨만 키 문자열로 떨어진다.
 */
export function applyAllRepeatHints(
  template: ReportTemplate,
  labels: Record<string, string>,
): { next: ReportTemplate; applied: RepeatHintApplied[]; failures: RepeatHintFailure[] } {
  const hints = [...(template.importMeta?.repeatHints ?? [])];
  let next = template;
  const applied: RepeatHintApplied[] = [];
  const failures: RepeatHintFailure[] = [];
  for (const hint of hints) {
    const r = applyRepeatHintToTemplate(next, hint, labels);
    if (r.error) {
      failures.push({ hint, error: r.error });
      continue;
    }
    next = r.next;
    applied.push({ hint, dsKey: r.dsKey ?? '', fieldCount: r.fieldCount ?? 0 });
  }
  return { next, applied, failures };
}
